import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, Channel } from '../lib/api'
import HudLoader from '../components/HudLoader'
import { LanguageSelect, MarketSelect } from '../lib/i18n'
import {
  Plus,
  RefreshCw,
  Edit,
  Trash2,
  ExternalLink,
  Shield,
  Loader2,
  Youtube,
  X,
  Link2,
} from 'lucide-react'
import { format } from 'date-fns'

function YoutubeBadge({ id }: { id: string }) {
  const { data } = useQuery({
    queryKey: ['yt-status', id],
    queryFn: () => api.get<any>(`/youtube/status?channel_id=${id}`),
    staleTime: 60000,
    retry: false,
  })
  if (!data) return <span className="badge badge-secondary">...</span>
  if (!data.configured) return <span className="badge badge-warning" title="YOUTUBE_CLIENT_ID/SECRET missing on server">Setup pending</span>
  if (!data.connected) return <span className="badge badge-secondary">Not connected</span>
  if (!data.valid) {
    return <span className="badge badge-danger" title={data.reason || 'Token invalid — dobara Connect dabao'}>Reconnect needed</span>
  }
  return <span className="badge badge-success">Connected</span>
}

export default function Channels() {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null)
  const [formData, setFormData] = useState({ title: '', youtube_channel_id: '', description: '', default_language: 'en', country: 'US', made_for_kids: false, niche: 'technology', priority: 0, review_required: false })

  const { data: channels, isLoading } = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<Channel[]>('/channels'),
  })

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => api.post<Channel>('/channels', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] })
      setShowModal(false)
      resetForm()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<typeof formData> }) => api.patch<Channel>(`/channels/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['channels'] })
      setEditingChannel(null)
      resetForm()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/channels/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channels'] }),
  })

  const syncMutation = useMutation({
    mutationFn: (id: string) => api.post<Channel>(`/channels/${id}/sync`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['channels'] }),
  })

  const connectYouTube = async (id: string) => {
    try {
      const data = await api.get<{ authorization_url: string }>(`/youtube/authorize?channel_id=${id}`)
      window.open(data.authorization_url, '_blank', 'width=600,height=700,noopener,noreferrer')
    } catch (e: any) {
      alert(e.message)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (editingChannel) {
      updateMutation.mutate({ id: editingChannel.id, data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const resetForm = () => {
    setFormData({ title: '', youtube_channel_id: '', description: '', default_language: 'en', country: 'US', made_for_kids: false, niche: 'technology', priority: 0, review_required: false })
  }

  const openCreate = () => {
    setEditingChannel(null)
    resetForm()
    setShowModal(true)
  }

  const openEdit = (channel: Channel) => {
    setEditingChannel(channel)
    setFormData({
      title: channel.title,
      youtube_channel_id: channel.youtube_channel_id,
      description: channel.description || '',
      default_language: channel.default_language,
      country: channel.country || 'US',
      made_for_kids: !!(channel as any).made_for_kids,
      niche: channel.niche || ((channel as any).made_for_kids ? 'kids' : 'technology'),
      priority: channel.priority ?? 0,
      review_required: !!channel.review_required,
    })
    setShowModal(true)
  }

  if (isLoading) return <HudLoader text="Loading channels" />

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Channels</h1>
          <p>Manage your YouTube channels</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>
          <Plus size={18} />
          Add Channel
        </button>
      </div>

      <div className="card">
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Channel</th>
                <th>Status</th>
                <th>YouTube</th>
                <th>YPP</th>
                <th>Subscribers</th>
                <th>Views</th>
                <th>Videos</th>
                <th>Language</th>
                <th>Audience</th>
                <th>Created</th>
                <th>Last Sync</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {channels?.map((channel) => (
                <tr key={channel.id}>
                  <td>
                    <div className="channel-info">
                      <div className="channel-avatar">{channel.title[0].toUpperCase()}</div>
                      <div>
                        <span className="channel-title">{channel.title}</span>
                        <span className="channel-id">@{channel.custom_url || channel.youtube_channel_id}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${channel.is_active ? 'badge-success' : 'badge-secondary'}`}>
                      {channel.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <YoutubeBadge id={channel.id} />
                  </td>
                  <td>
                    <span className={`badge ${channel.ypp_eligible ? 'badge-success' : 'badge-warning'}`}>
                      {channel.ypp_eligible ? <Shield size={12} /> : <Shield size={12} />}
                      {channel.ypp_eligible ? 'Eligible' : 'Not Eligible'}
                    </span>
                  </td>
                  <td>{channel.subscriber_count.toLocaleString()}</td>
                  <td>{channel.view_count.toLocaleString()}</td>
                  <td>{channel.video_count}</td>
                  <td>{channel.default_language}</td>
                  <td>
                    {channel.made_for_kids
                      ? <span className="badge badge-info" title="Uploads flagged selfDeclaredMadeForKids (COPPA)">Kids</span>
                      : <span className="badge badge-secondary">General</span>}
                  </td>
                  <td>{channel.created_at ? format(new Date(channel.created_at), 'MMM dd, yyyy HH:mm') : '-'}</td>
                  <td>{channel.last_synced_at ? format(new Date(channel.last_synced_at), 'MMM dd, yyyy HH:mm') : 'Never'}</td>
                  <td>
                    <div className="action-menu">
                      <button className="icon-btn" onClick={() => syncMutation.mutate(channel.id)} disabled={syncMutation.isPending} title="Sync stats">
                        {syncMutation.isPending && syncMutation.variables === channel.id ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
                      </button>
                      <button className="icon-btn" onClick={() => connectYouTube(channel.id)} title="Connect YouTube (OAuth)">
                        <Link2 size={16} />
                      </button>
                      <button className="icon-btn" onClick={() => openEdit(channel)} title="Edit">
                        <Edit size={16} />
                      </button>
                      <button className="icon-btn" onClick={() => window.open(`https://youtube.com/channel/${channel.youtube_channel_id}`, '_blank', 'noopener,noreferrer')} title="Open on YouTube">
                        <ExternalLink size={16} />
                      </button>
                      <button className="icon-btn danger" onClick={() => deleteMutation.mutate(channel.id)} disabled={deleteMutation.isPending} title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!channels?.length && (
          <div className="empty-state">
            <Youtube size={48} />
            <h3>No channels yet</h3>
            <p>Add your first YouTube channel to start automating</p>
            <button className="btn btn-primary" onClick={openCreate}>
              <Plus size={18} />
              Add Channel
            </button>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); resetForm(); }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingChannel ? 'Edit Channel' : 'Add Channel'}</h2>
              <button className="icon-btn" onClick={() => { setShowModal(false); resetForm(); }}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="My Tech Channel"
                />
              </div>
                <div className="form-group">
                  <label htmlFor="youtube_channel_id">YouTube Channel ID *</label>
                  <input
                    id="youtube_channel_id"
                    value={formData.youtube_channel_id}
                    onChange={(e) => setFormData({ ...formData, youtube_channel_id: e.target.value.trim() })}
                    required
                    pattern="UC[A-Za-z0-9_-]{22}"
                    title="24 characters, UC se shuru (jaise UCbFpK2bw1Px4WL6T9AYxBHw)"
                    placeholder="UCxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  <p className="form-help">YouTube Studio &gt; Settings &gt; Channel &gt; Advanced Settings me milega. @handle nahi chalega.</p>
                </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Channel description..."
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="default_language">Default Language (50+ supported)</label>
                  <LanguageSelect
                    id="default_language"
                    value={formData.default_language}
                    onChange={(v) => setFormData({ ...formData, default_language: v })}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="country">Country (40+ markets)</label>
                  <MarketSelect
                    id="country"
                    value={formData.country}
                    onChange={(v) => setFormData({ ...formData, country: v })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="niche">Content Niche *</label>
                <select
                  id="niche"
                  value={formData.made_for_kids ? 'kids' : formData.niche}
                  onChange={(e) => setFormData({ ...formData, niche: e.target.value })}
                  disabled={formData.made_for_kids}
                >
                  <option value="technology">Technology</option>
                  <option value="kids">Kids & Family</option>
                  <option value="space">Space & Science</option>
                  <option value="education">Education</option>
                  <option value="gaming">Gaming</option>
                </select>
                <p className="form-help">Auto-pilot isi niche ke topics discover karega. Space channel ke liye Space & Science chuno.</p>
              </div>
              <div className="form-group">
                <label htmlFor="priority">Priority (0-100) *</label>
                <input
                  id="priority"
                  type="number"
                  min={0}
                  max={100}
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                />
                <p className="form-help">Zyada number = auto-pilot pehle (Tech Tricks ko 10 do — hamesha #1 rahega).</p>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={formData.made_for_kids}
                    onChange={(e) => setFormData({ ...formData, made_for_kids: e.target.checked, niche: e.target.checked ? 'kids' : formData.niche })}
                  />
                  Made for Kids channel (COPPA)
                </label>
                <p className="form-help">ON rakho agar channel bachchon ke liye hai — uploads auto-flag honge, revenue limited hoga (no personalized ads).</p>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    style={{ width: 'auto' }}
                    checked={formData.review_required}
                    onChange={(e) => setFormData({ ...formData, review_required: e.target.checked })}
                  />
                  Review before upload (preview + approve)
                </label>
                <p className="form-help">ON: compliance ke baad upload ruk jayega — Videos page par preview dekh kar Approve/Reject karo. OFF (default) = hands-free automation waise hi chalti rahegi.</p>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={createMutation.isPending || updateMutation.isPending}>
                  {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (editingChannel ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}